import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';

import { AuthProtectedButton } from 'common/components';
import { UserAvatar } from 'common/components/UserCard/UserAvatar.component';

import { useBowser, useI18n } from 'common/hooks';
import { VerificationLevelStates } from 'common/hooks/useCustomer/useCustomer.constants';
import { useCustomer } from 'common/hooks/useCustomer/useCustomer.hook';
import svgIcons from 'common/svgIcons';

import { routes } from 'routes/routes.constants';

import SvgIconUrl from '../SvgIconUrl/SvgIconUrl.component';
import './UserCard.css';

type Props = {
  className?: string;
  onClick?: () => void;
};

export const UserCard: React.FC<React.PropsWithChildren<Props>> = ({ className = '', onClick }) => {
  const { intermediateVerificationLevelStatus, email = 'Guest', isAuthenticated } = useCustomer();
  const { isDesktop, isMobile } = useBowser();
  const { translate } = useI18n();
  const { push } = useHistory();

  const userClassNames = classNames({
    user: true,
    [className]: className,
  });

  const isVerified = useMemo(
    () => intermediateVerificationLevelStatus.verificationLevelState === VerificationLevelStates.COMPLETED,
    [intermediateVerificationLevelStatus.verificationLevelState],
  );
  const isNotVerified = useMemo(
    () => intermediateVerificationLevelStatus.verificationLevelState !== VerificationLevelStates.COMPLETED,
    [intermediateVerificationLevelStatus.verificationLevelState],
  );

  const verificationClassNames = useMemo(
    () =>
      classNames({
        user__verification: true,
        Body4_small_reg_12: isDesktop,
        Body2_reg_14: isMobile,
        'user__verification--verified': isVerified,
        'user__verification--unverified': isNotVerified,
      }),
    [isDesktop, isMobile, isVerified, isNotVerified],
  );

  const emailClassNames = useMemo(
    () =>
      classNames({
        user__name: true,
        H4_med14: isDesktop,
        Body4_small_reg_12: isMobile,
      }),
    [isDesktop, isMobile],
  );

  const verificationStatusLevelShortLabel = useMemo(() => {
    if (!isAuthenticated) return translate.toText('mobile:current:user:status:unverified');
    return translate.toText(
      `mobile:user:verification:${String(intermediateVerificationLevelStatus.verificationLevel).replace(
        /\s/g,
        '-',
      )}:short`,
    );
  }, [isAuthenticated, intermediateVerificationLevelStatus.verificationLevel, translate]);

  const onClickCard = useCallback(
    () => (onClick ? onClick() : push(routes.getVerificationLevelPage())),
    [onClick, push],
  );

  const renderVerificationStatus = useCallback(() => {
    if (!isAuthenticated) return null;

    switch (intermediateVerificationLevelStatus.verificationLevelState) {
      case VerificationLevelStates.COMPLETED:
        return translate('mobile:user:verified');
      case VerificationLevelStates.IN_PROGRESS:
        return translate('mobile:user:in-progress');
      case VerificationLevelStates.NOT_APPROVED:
      default:
        return translate('mobile:user:declined');
    }
  }, [isAuthenticated, intermediateVerificationLevelStatus.verificationLevelState, translate]);

  return (
    <AuthProtectedButton>
      <div onClick={onClickCard} className={userClassNames}>
        <div className='user__top'>
          <UserAvatar />
          <span className='user__info'>
            <p data-test='user-info' className={emailClassNames}>
              {email}
            </p>
          </span>
        </div>
        <div className='user__bottom Body2_reg_14'>
          <div className={verificationClassNames}>
            <span data-test='verification-status'>
              {verificationStatusLevelShortLabel} {renderVerificationStatus()}
            </span>
            <SvgIconUrl
              data-test='verification-icon'
              className='user__verification-icon'
              symbol={isVerified ? svgIcons.Verified() : svgIcons.Warning()}
            />
          </div>
        </div>
      </div>
    </AuthProtectedButton>
  );
};

export default React.memo(UserCard);
